import React from 'react';
import { Tag } from 'antd';
import { humanizeKey, labelFor } from './analysis/analysisUtils';

const MAX_DEPTH = 6;
const EMPTY_TEXT_VALUES = new Set(['ندارد', '']);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const KeyLabel = ({ name }) => {
  const label = labelFor(name);
  // کلید بدون برچسب فارسی، به صورت چپ‌چین و تک‌فاصله نمایش داده می‌شود
  const untranslated = label === humanizeKey(name);
  return (
    <span
      className={`text-xs text-gray-500 dark:text-gray-400 shrink-0 min-w-[120px] leading-6 ${untranslated ? 'font-mono' : 'font-medium'}`}
      dir={untranslated ? 'ltr' : undefined}
    >
      {label}
    </span>
  );
};

const Muted = ({ children }) => <span className="text-gray-400 text-sm">{children}</span>;

const ValueView = ({ value, depth }) => {
  if (value === null || value === undefined) return <Muted>—</Muted>;

  if (typeof value === 'boolean') {
    return <Tag color={value ? 'success' : 'default'} className="custom-font m-0 text-xs">{value ? 'بله' : 'خیر'}</Tag>;
  }

  if (typeof value === 'number') {
    return <span className="text-sm font-medium" dir="ltr">{value.toLocaleString('fa-IR')}</span>;
  }

  if (typeof value === 'string') {
    return EMPTY_TEXT_VALUES.has(value.trim())
      ? <Muted>{value.trim() || '—'}</Muted>
      : <span className="text-sm text-gray-800 dark:text-gray-100 break-words leading-6 whitespace-pre-wrap">{value}</span>;
  }

  if (depth >= MAX_DEPTH) {
    return <code className="text-xs break-all" dir="ltr">{JSON.stringify(value)}</code>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <Muted>[]</Muted>;

    if (value.every((item) => !isPlainObject(item) && !Array.isArray(item))) {
      return (
        <span className="inline-flex flex-wrap gap-1">
          {value.map((item, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Tag key={idx} className="custom-font m-0 text-xs">{String(item)}</Tag>
          ))}
        </span>
      );
    }

    return (
      <div className="space-y-2 w-full">
        {value.map((item, idx) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx} className="p-2.5 rounded-lg border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60">
            <ValueView value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <ObjectView data={value} depth={depth + 1} />;
};

const ObjectView = ({ data, depth }) => (
  <div className="space-y-1.5 w-full min-w-0">
    {Object.entries(data).map(([key, value]) => {
      const nested = isPlainObject(value) || (Array.isArray(value) && value.some(isPlainObject));
      return nested ? (
        <div key={key} className="pt-1">
          <KeyLabel name={key} />
          <div className="mt-1 ps-3 border-s-2 border-purple-100 dark:border-purple-900/60">
            <ValueView value={value} depth={depth} />
          </div>
        </div>
      ) : (
        <div key={key} className="flex items-start gap-2">
          <KeyLabel name={key} />
          <ValueView value={value} depth={depth} />
        </div>
      );
    })}
  </div>
);

/**
 * نمایش عمومی هر ساختار JSON تحلیل مکالمه (برای قالب خروجی سفارشی که کلیدهای آن در پرامپت تعریف می‌شوند).
 */
const AiAnalysisTree = ({ data }) => (isPlainObject(data) ? <ObjectView data={data} depth={0} /> : null);

export default AiAnalysisTree;
